import { useEffect, useState } from 'react';
import type { Subscription } from '../../types';
import Spinner from '../common/Spinner';

interface Props {
  status: NonNullable<Subscription['dedicated_ip_status']> | null | undefined;
  ip: string | null | undefined;
}

export default function DedicatedIpDisplay({ status, ip }: Props) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(id);
  }, [copied]);

  const copy = async () => {
    if (!ip) return;
    await navigator.clipboard.writeText(ip);
    setCopied(true);
  };

  if (!status) return null;

  if (status === 'active' && ip) {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-gray-900">
        <span aria-label="active" className="w-2 h-2 rounded-full bg-green-500" />
        <span className="font-mono">{ip}</span>
        <button
          type="button"
          onClick={copy}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </span>
    );
  }

  if (status === 'provisioning') {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-gray-500">
        <Spinner size="sm" />
        Activating...
      </span>
    );
  }

  if (status === 'releasing') {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-gray-500">
        <Spinner size="sm" />
        Releasing...
      </span>
    );
  }

  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-amber-600">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
        IP issue — contact support
      </span>
    );
  }

  return null;
}
